import * as z from "zod";

import { Activity, UnitOfTime } from "@/types/policy.enum";
import type { City, ColorInput, Policy } from "@/types";
import type {
  CreatePolicyMutation,
  ColorInput as GqlColorInput,
  CreatePolicyInput as GqlCreatePolicyInput,
  PolicyRuleInput,
  TimeSpanInput,
} from "@/types/gql/graphql";
import { DaysOfWeek, PolicyCategory } from "@/types/gql/graphql";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { arrCategory, arrCategoryDropdown, defaultCategory, defaultRule } from "../policies.const";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { DropdownAutoCompleteSelect } from "@/components/form-control/dropdown-autocomplete";
import { Input } from "@/components/ui/input";
import { InputNumber } from "@/components/form-control/input-number";
import { PolicyColorInput } from "./PolicyColorInput";
import { RuleListing } from "./RuleListing";
import { TimeSpanListing } from "@/components/time-span";
import { colors } from "@/constants/map.const";
import { defaultTimeSpan } from "@/components/time-span/time-span.const";
import { findObject } from "@/helper/array";
import { fromGraphQLColor } from "@/helper/utils";
import { getEnumKeyByEnumValue } from "@/helper/format-data";
import { useForm } from "react-hook-form";
import { usePolicies } from "../hooks/use-policies";
import { zodResolver } from "@hookform/resolvers/zod";

const policySchema = z.object({
  name: z.string().min(1, "Name is required"),
  priority: z.number().min(1),
  category: z.enum(PolicyCategory),
  timeSpans: z
    .array(
      z.object({
        daysOfWeek: z.array(z.enum(DaysOfWeek)).optional().nullable(),
        daysOfMonth: z.array(z.number()).optional().nullable(),
        months: z.array(z.number()).optional().nullable(),
        timeOfDayStart: z.string().optional(),
        timeOfDayEnd: z.string().optional(),
      }),
    )
    .optional(),
});

type PolicyFormValues = z.infer<typeof policySchema>;

export type AddPolicyProps = {
  policyData: Policy;
  onClose: () => void;
  currentCity: City;
  onSubmit?: (data: CreatePolicyMutation) => void;
};

export const AddPolicyModal = ({ policyData, onClose, currentCity, onSubmit }: AddPolicyProps) => {
  const [rules, setRules] = useState(policyData.rules);
  const [policyColor, setPolicyColor] = useState<ColorInput>(() => ({
    fillColor: fromGraphQLColor(policyData.color?.fillColor, colors.PAYANT.fillColor),
    lineColor: fromGraphQLColor(policyData.color?.lineColor, colors.PAYANT.lineColor),
    dashed: policyData.color?.dashed ?? colors.PAYANT.dashedBorder,
    iconColor: policyData?.color?.iconColor ?? undefined,
  }));
  const form = useForm<PolicyFormValues>({
    resolver: zodResolver(policySchema),
    defaultValues: {
      name: policyData.name,
      priority: policyData.priority,
      category: policyData?.category ?? defaultCategory.key,
      timeSpans:
        policyData.timeSpans?.map((span) => ({
          daysOfWeek: span.daysOfWeek ?? undefined,
          daysOfMonth: span.daysOfMonth ?? undefined,
          months: span.months ?? undefined,
          timeOfDayStart: span.timeOfDayStart ?? undefined,
          timeOfDayEnd: span.timeOfDayEnd ?? undefined,
        })) ?? [],
    },
    mode: "all",
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = form;

  const { handleAddPolicy, handleUpdatePolicy } = usePolicies();

  const savePolicy = async (values: PolicyFormValues) => {
    const newRules: PolicyRuleInput[] = rules.map((item) => {
      const newActivity = getEnumKeyByEnumValue(Activity, item.activity);
      return {
        ...item,
        activity: newActivity,
      } as PolicyRuleInput;
    });
    const fCategoryIndex = findObject(arrCategory, "key", values.category as PolicyCategory);
    const category = arrCategory[fCategoryIndex].key as PolicyCategory;

    const gqlTimeSpans: TimeSpanInput[] | undefined = values.timeSpans?.map((span) => ({
      ...span,
      daysOfWeek: span.daysOfWeek ?? undefined,
    }));

    const gqlColor: GqlColorInput = {
      dashed: policyColor.dashed,
      lineColor: policyColor.lineColor,
      fillColor: policyColor.fillColor,
      iconColor: policyColor.iconColor,
    };

    const newPolicy: GqlCreatePolicyInput = {
      ...values,
      category,
      cityId: currentCity.id,
      rules: newRules,
      color: gqlColor,
      timeSpans: gqlTimeSpans,
    };

    if (policyData.id) {
      await handleUpdatePolicy({ updatePolicyInput: { ...newPolicy, id: policyData.id } });
    } else {
      const res = await handleAddPolicy({ createPolicyInput: newPolicy });
      if (res) {
        onSubmit?.(res);
      }
    }
    onClose();
  };

  const handleSaveColor = (value: ColorInput) => {
    setPolicyColor(value);
  };

  const category = form.watch("category");

  useEffect(() => {
    if (policyData.id) return;
    //For case: create a new policy, get default value from configuration variable
    const fCategoryIndex = findObject(arrCategory, "key", category);
    if (fCategoryIndex < 0) return;

    const { value } = arrCategory[fCategoryIndex];
    if (value) {
      const {
        activity,
        priority,
        timeOfDayEnd,
        timeOfDayStart,
        userClasses,
        maxStay = 0,
        maxStayUnit = UnitOfTime.Minute,
      } = value;
      setValue("priority", priority);
      setValue("timeSpans", [
        {
          ...defaultTimeSpan,
          timeOfDayStart,
          timeOfDayEnd,
        },
      ]);
      setRules([{ ...defaultRule, activity, userClasses, maxStay, maxStayUnit }]);
    } else {
      setValue("priority", 1);
      setValue("timeSpans", []);
      setRules([]);
    }
  }, [category, policyData.id, setValue]);

  return (
    <Dialog open>
      <DialogContent className="max-h-4/5 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{`${policyData.id ? "Edit" : "Add"} a policy`}</DialogTitle>
          <DialogDescription>Policy information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-6">
            <FormField
              control={control}
              name="category"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>
                    Category <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <DropdownAutoCompleteSelect
                      className="justify-between w-full"
                      value={value}
                      options={arrCategoryDropdown}
                      onChangeValue={onChange}
                      placeholder="Search a category"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Priority<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <InputNumber {...field} min={1} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <PolicyColorInput policyColor={policyColor} onSaveColor={handleSaveColor} />

            <FormField
              control={control}
              name="timeSpans"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Time Spans</FormLabel>
                  <FormControl>
                    <div>
                      <TimeSpanListing timeSpans={value} setTimeSpans={onChange} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <RuleListing rules={rules} setRules={setRules} />

            <DialogFooter className="sm:justify-between">
              <Button onClick={onClose} variant="secondary" type="button">
                Cancel
              </Button>
              <Button disabled={isSubmitting} onClick={handleSubmit(savePolicy)}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
